export type FileReviewDecision = {
  title: string;
  filename: string;
  lineStart: number | null;
  lineEnd: number | null;
};

export type FileReviewDecisionsByFilename = Map<string, FileReviewDecision[]>;

export type PrV2FileDescriptionResponse =
  | {
      status: 'ready';
      decisions: FileReviewDecision[];
    }
  | {
      status: 'pending';
    }
  | {
      status: 'unsupported';
    };

export type FileReviewDecisionGlyphChar = '?' | '!' | '!?';

export type FileReviewDecisionLineGroup = {
  lineStart: number;
  lineEnd: number;
  decisions: FileReviewDecision[];
};

export function buildFileReviewDecisionsByFilename(
  decisions: readonly FileReviewDecision[] | undefined,
): FileReviewDecisionsByFilename {
  const decisionsByFilename: FileReviewDecisionsByFilename = new Map();

  for (const decision of decisions ?? []) {
    const fileDecisions = decisionsByFilename.get(decision.filename) ?? [];
    fileDecisions.push(decision);
    decisionsByFilename.set(decision.filename, fileDecisions);
  }

  return decisionsByFilename;
}

export function getReviewDecisionFilenames(
  decisions: readonly FileReviewDecision[] | undefined,
): Set<string> {
  return new Set((decisions ?? []).map((decision) => decision.filename));
}

export function formatFileReviewDecisionLabel(
  decisions: readonly { title: string }[] | undefined,
  { separator = '\n' }: { separator?: string } = {},
): string | undefined {
  const titles = decisions?.map((decision) => decision.title.trim()).filter(Boolean) ?? [];

  if (titles.length === 0) {
    return undefined;
  }

  if (titles.length === 1) {
    return titles[0];
  }

  return titles.map((title, index) => `${index + 1}. ${title}`).join(separator);
}

/** "line 41" or "lines 41-43", for every place that names where a decision sits. */
export function formatFileReviewDecisionLines(lineStart: number, lineEnd: number | null): string {
  return lineEnd !== null && lineEnd > lineStart
    ? `lines ${lineStart}-${lineEnd}`
    : `line ${lineStart}`;
}

// Decisions are never warnings about defects, so the glyph is punctuation, not a warning
// triangle (Paul, Jul 9). It matches the item's shape: a choice is a question we ask the
// reviewer ("?"), a consequence is a fact they must consciously accept ("!"), and a line
// hosting both shows "!?" - chess notation for "interesting, deserves attention". Shared by
// every decision affordance (inline marker, file tree) so the visual language stays one system.
export function getFileReviewDecisionGlyphChar(
  decisions: readonly { title: string }[] | undefined,
): FileReviewDecisionGlyphChar {
  let hasChoice = false;
  let hasConsequence = false;
  for (const decision of decisions ?? []) {
    if (decision.title.trimEnd().endsWith('?')) {
      hasChoice = true;
    } else {
      hasConsequence = true;
    }
  }

  if (hasChoice && hasConsequence) {
    return '!?';
  }
  return hasConsequence ? '!' : '?';
}

/**
 * Groups a file's anchored review decisions by start line - one diff marker per line.
 * File-level decisions (null lineStart) are excluded: they render via the file tree only.
 */
export function groupAnchoredDecisionsByLine(
  decisions: readonly FileReviewDecision[] | undefined,
): FileReviewDecisionLineGroup[] {
  if (!decisions?.length) {
    return [];
  }

  const groupsByLine = new Map<number, FileReviewDecisionLineGroup>();
  for (const decision of decisions) {
    if (decision.lineStart == null) {
      continue;
    }

    const lineStart = decision.lineStart;
    // Anchors are validated server-side, but a lone lineStart is still a valid single-line anchor.
    const lineEnd = Math.max(decision.lineEnd ?? lineStart, lineStart);
    const group = groupsByLine.get(lineStart);
    if (group) {
      group.decisions.push(decision);
      group.lineEnd = Math.max(group.lineEnd, lineEnd);
    } else {
      groupsByLine.set(lineStart, { lineStart, lineEnd, decisions: [decision] });
    }
  }

  return [...groupsByLine.values()].sort((a, b) => a.lineStart - b.lineStart);
}
